import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import AppointmentDetailModal from "../components/AppointmentDetailModal";

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(dayjs());
  const [selectedAppt, setSelectedAppt] = useState(null);
  const navigate = useNavigate();

  const openAppt = (a) => {
    if (a.patient_id) navigate(`/patients/${a.patient_id}`);
    else setSelectedAppt(a);
  };

  useEffect(() => {
    api.get("/appointments").then((r) => setItems(r.data));
  }, []);

  const byDate = useMemo(() => {
    const m = {};
    for (const a of items) {
      const k = dayjs(a.scheduled_at).format("YYYY-MM-DD");
      if (!m[k]) m[k] = [];
      m[k].push(a);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    return m;
  }, [items]);

  // ---- Month grid ----
  const monthGrid = useMemo(() => {
    const start = cursor.startOf("month").startOf("week");
    const end = cursor.endOf("month").endOf("week");
    const days = [];
    let d = start;
    while (d.isBefore(end) || d.isSame(end, "day")) {
      days.push(d);
      d = d.add(1, "day");
    }
    return days;
  }, [cursor]);

  // ---- Week grid ----
  const weekDays = useMemo(() => {
    const start = cursor.startOf("week");
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  }, [cursor]);

  return (
    <div className="space-y-6 fade-up">
      <header>
        <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
          Schedule
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Calendar</h1>
        <p className="text-muted-foreground mt-2">
          Visualise upcoming appointments by week or month.
        </p>
      </header>

      <Tabs defaultValue="month">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="month" data-testid="cal-tab-month">Month</TabsTrigger>
            <TabsTrigger value="week" data-testid="cal-tab-week">Week</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCursor(cursor.subtract(1, "month"))}
              data-testid="cal-prev"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setCursor(dayjs())}
              data-testid="cal-today"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCursor(cursor.add(1, "month"))}
              data-testid="cal-next"
            >
              <ChevronRight className="size-4" />
            </Button>
            <div className="font-heading text-lg font-semibold ml-3 min-w-[140px]">
              {cursor.format("MMMM YYYY")}
            </div>
          </div>
        </div>

        <TabsContent value="month" className="pt-4">
          <Card className="p-2">
            <div className="grid grid-cols-7 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="px-2 py-2 text-center">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-border">
              {monthGrid.map((d) => {
                const k = d.format("YYYY-MM-DD");
                const list = byDate[k] || [];
                const inMonth = d.month() === cursor.month();
                const isToday = d.isSame(dayjs(), "day");
                return (
                  <div
                    key={k}
                    data-testid={`cal-day-${k}`}
                    className={`min-h-[110px] bg-white p-2 transition-colors ${
                      !inMonth ? "opacity-40" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-sm font-medium ${
                          isToday
                            ? "bg-primary text-primary-foreground rounded-full size-6 grid place-items-center"
                            : ""
                        }`}
                      >
                        {d.format("D")}
                      </span>
                      {list.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1.5">
                          {list.length}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1">
                      {list.slice(0, 2).map((a) => (
                        <button
                          key={a.id}
                          onClick={() => openAppt(a)}
                          data-testid={`cal-appt-${a.id}`}
                          className="block w-full text-left px-1.5 py-1 rounded text-[11px] bg-primary/10 text-primary hover:bg-primary/15 truncate"
                        >
                          {a.display_time || dayjs(a.scheduled_at).format("h:mm A")} · {a.patient_name}
                        </button>
                      ))}
                      {list.length > 2 && (
                        <div className="text-[11px] text-muted-foreground pl-1.5">
                          +{list.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="week" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {weekDays.map((d) => {
              const k = d.format("YYYY-MM-DD");
              const list = byDate[k] || [];
              const isToday = d.isSame(dayjs(), "day");
              return (
                <Card
                  key={k}
                  className={`p-3 min-h-[200px] ${isToday ? "border-primary" : ""}`}
                  data-testid={`week-day-${k}`}
                >
                  <div className="flex items-baseline gap-2 mb-3">
                    <p className="text-xs uppercase text-muted-foreground tracking-wider">
                      {d.format("ddd")}
                    </p>
                    <p
                      className={`font-heading text-xl font-bold ${
                        isToday ? "text-primary" : ""
                      }`}
                    >
                      {d.format("D")}
                    </p>
                  </div>
                  {list.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No appointments</p>
                  ) : (
                    <div className="space-y-2">
                      {list.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => openAppt(a)}
                          data-testid={`week-appt-${a.id}`}
                          className="block w-full text-left p-2 rounded-md border border-border hover:border-primary/40 transition-colors"
                        >
                          <p className="text-xs font-semibold">
                            {a.display_time || dayjs(a.scheduled_at).format("h:mm A")}
                          </p>
                          <p className="text-sm truncate">{a.patient_name}</p>
                          {a.reason && (
                            <p className="text-[11px] text-muted-foreground truncate">
                              {a.reason}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {items.length === 0 && (
        <Card className="p-10 text-center border-dashed">
          <CalIcon className="size-7 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">
            No appointments yet. Book one from a patient profile.
          </p>
        </Card>
      )}

      <AppointmentDetailModal
        appointment={selectedAppt}
        open={!!selectedAppt}
        onClose={() => setSelectedAppt(null)}
      />
    </div>
  );
}
